/***
 *
 *   INPUT
 *   Text input for all types (phone, url, email, etc)
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/form
 *   https://ui.shadcn.com/docs/components/input
 *
 *   PROPS
 *   aria-describedby: id of the element that describes the input in ...props (string, optional)
 *   aria-invalid: determines if the input is invalid in ...props (boolean, required)
 *   className: custom styling (SCSS or tailwind style, optional)
 *   defaultValue: initial value (string, optional)
 *   disabled: disable the input (boolean, optional)
 *   name: input name (string, required)
 *   placeholder: placeholder value (string, optional)
 *   type: input type (string, required, default: text)
 *   value: current value (string, optional)
 *
 **********/

import { forwardRef, useState } from "react";
import { cn } from "components/lib";

const Input = forwardRef(
  (
    {
      className,
      name,
      defaultValue,
      value,
      type,
      placeholder,
      label,
      onChange,
      disabled,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const hasValue = (value || defaultValue) && (value || defaultValue).toString().trim() !== '';

    return (
      <div className="relative">
        <input
          name={name}
          ref={ref}
          autoComplete={name}
          disabled={disabled}
          type={type || "text"}
          placeholder={focused ? placeholder : ""}
          className={cn(
            "glass-input h-12 w-full px-3 py-3 text-white placeholder-white/50 text-sm font-light tracking-wide transition-all duration-300 focus:outline-none",
            "focus:glass-input focus:border-blue-400/60 focus:shadow-lg focus:shadow-blue-400/20",
            "autofill:text-white autofill:!text-white",
            className,
            props["aria-invalid"] && "border-red-400/60 focus:border-red-400/60",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          value={value || ""}
          defaultValue={defaultValue}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) =>
            onChange({
              type: "change",
              target: {
                name: name,
                value: type === "file" ? e.target?.files : e.target.value,
              },
            })
          }
          {...props}
        />
        {label && (
          <label
            htmlFor={name}
            className={cn(
              "floating-label",
              (focused || hasValue) && "active focused",
              props["aria-invalid"] && "text-red-400"
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
export { Input };
